import {useState, useRef, memo} from 'react';
import * as React from 'react';
import type {MouseEvent, ChangeEvent} from 'react';
import {
  Banner,
  EmptySearchResult,
  List,
  Modal,
  Pagination,
  Select,
  Tabs,
  TextField,
  Text,
  Link,
} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {Image} from '@web-utilities/types';
import {useUrl, isValid, isHttpSecure, getSizedImage} from '@web-utilities/url';
import {showDefaultCursor, showProgressCursor} from 'utilities/cursor';
import {StagedUploadTargetGenerateUploadResource} from 'types/graphql/default-types';
import {nodesFromEdges} from '@shopify/admin-graphql-api-utilities';
import {useToast} from 'utilities/toasts';
import {useAppBridge} from '@shopify/app-bridge-react';

import {Tab, ImageSize} from '../../types';

import {useInsertImageModalQuery, useAddShopImageMutation} from './hooks';
import {
  FileUpload,
  ImageGrid,
  MediaUploadResult,
  MediaUploader,
  MediaUploadStatus,
  validateMediaResults,
} from './components';

export interface Props {
  open: boolean;
  onClose(): void;
  onInsertImage(image: Image): void;
}

function InsertImageModalComponent({open, onClose, onInsertImage}: Props) {
  const [i18n] = useI18n();
  const url = useUrl();
  const fileUploadInput = useRef<HTMLInputElement | null>(null);
  const app = useAppBridge();

  const {show} = useToast();
  const handleToast = (content: string, error = false) => {
    show({
      content,
      error,
    });
  };

  const [selectedTab, setSelectedTab] = useState<Tab>(Tab.UploadedImages);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedImagesAltText, setUploadedImagesAltText] = useState('');
  const [activeImage, setActiveImage] = useState<Image | null>(null);
  const [imageSize, setImageSize] = useState<ImageSize>(ImageSize.ProductImage);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [imageToUpload, setImageToUpload] = useState<File | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);

  const {
    data,
    loading: queryLoading,
    error,
    refetch,
    handlePaginateProductImagesPrevious,
    handlePaginateProductImagesNext,
    handlePaginateUploadedImagesNext,
    handlePaginateUploadedImagesPrevious,
  } = useInsertImageModalQuery(open, handleToast, setActiveImage);

  const {handleUploadImageFromUrl, handleUploadImage} =
    useAddShopImageMutation(handleToast);

  const imageSizeOptions = [
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.original'),
      value: ImageSize.Original,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.inline'),
      value: ImageSize.Inline,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.icon'),
      value: ImageSize.Icon,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.thumb'),
      value: ImageSize.Thumb,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.smallLogo'),
      value: ImageSize.SmallLogo,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.logo'),
      value: ImageSize.Logo,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.productThumb'),
      value: ImageSize.ProductThumb,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.productImage'),
      value: ImageSize.ProductImage,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.bannerImage'),
      value: ImageSize.BannerImage,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.wallpaper1024'),
      value: ImageSize.WallPaper1024,
    },
    {
      label: i18n.translate('InsertImageModal.imageSizeOptions.wallpaper2048'),
      value: ImageSize.WallPaper2048,
    },
  ];
  const tabList = [
    {
      id: 'uploaded-images',
      content: i18n.translate('InsertImageModal.uploadedImages'),
    },
    {
      id: 'product-images',
      content: i18n.translate('InsertImageModal.productImages'),
    },
    {
      id: 'url',
      content: i18n.translate('InsertImageModal.url'),
    },
  ];

  const handleOnClose = () => {
    onClose();
    setSelectedTab(Tab.UploadedImages);
    setImageUrl('');
    setUploadedImagesAltText('');
    setActiveImage(null);
    setImageSize(ImageSize.ProductImage);
    setValidationError(null);
    setUploadInProgress(false);
    setImageToUpload(undefined);
  };

  const onImageClick = (newActiveImage: Image) => {
    setActiveImage(newActiveImage);
  };

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    setActiveImage(null);
  };

  const handleUrlChange = (newUrl: string) => {
    setImageUrl(newUrl);
  };

  const handleImageSizeChange = (newImageSize: ImageSize) => {
    setImageSize(newImageSize);
  };

  const handleUploadedImagesAltTextChange = (newAltText: string) => {
    setUploadedImagesAltText(newAltText);
  };

  const handleInsertImageAction = async () => {
    if (onInsertImage == null) {
      return;
    }
    if (selectedTab === Tab.Url) {
      if (isHttpSecure(imageUrl)) {
        const newImage: Image = {
          src: imageUrl,
        };
        onInsertImage(newImage);
      } else {
        startLoading();
        await handleUploadImageFromUrl({
          url: imageUrl,
          loading: queryLoading,
          setValidationError,
          refetch,
          onInsertImage,
        });
        stopLoading();
      }
    } else if (activeImage != null) {
      const newImage: Image = {
        id: activeImage.id,
        src: getSizedImage(activeImage.src, imageSize),
        altText: uploadedImagesAltText,
      };
      onInsertImage(newImage);
    }
    setSelectedTab(Tab.UploadedImages);
    setImageUrl('');
    setActiveImage(null);
    setImageSize(ImageSize.ProductImage);
    setUploadedImagesAltText('');
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {files} = event.target;
    processSelectedImage(files);
  };

  const handleAddImageClick = () => {
    if (!fileUploadInput.current) {
      return;
    }
    fileUploadInput.current.click();
  };

  const startLoading = () => {
    setLoading(true);
    showProgressCursor();
    setUploadInProgress(true);
  };

  const stopLoading = () => {
    setLoading(false);
    showDefaultCursor();
    setUploadInProgress(false);
  };

  const handleUploadImageFinished = async (results: MediaUploadResult[]) => {
    const image = results[0];
    if (image.status === MediaUploadStatus.ValidationFailed) {
      const validationError = validateMediaResults(
        image.error,
        image.media.name,
      );
      setImageToUpload(undefined);
      setValidationError(
        validationError
          ? i18n.translate(
              validationError?.error,
              validationError?.replacements,
            )
          : null,
      );

      stopLoading();
      return;
    }
    setImageToUpload(undefined);
    await handleUploadImage({
      result: image,
      loading: queryLoading,
      hasPreviousPage: data!.shop.uploadedImages.pageInfo.hasPreviousPage,
      refetch,
      error,
    });
    stopLoading();
  };
  const processSelectedImage = (image: FileList | null) => {
    if (image == null || image.length === 0) {
      return;
    }
    setValidationError(null);
    setImageToUpload(image[0]);
    startLoading();
  };

  let currentTabMarkup = null;
  let paginationMarkup: React.ReactNode = null;
  const imageUploaderMarkup =
    imageToUpload == null ? null : (
      <MediaUploader
        medias={[imageToUpload]}
        resourceType={StagedUploadTargetGenerateUploadResource.ShopImage}
        onAllMediasUploaded={handleUploadImageFinished}
        validate
      />
    );
  const errorBanner =
    validationError == null ? null : (
      <Modal.Section>
        <Banner
          title={i18n.translate('InsertImageModal.errorBannerTitle')}
          tone="warning"
          action={{
            content: i18n.translate('InsertImageModal.tryAnotherImage'),
            onAction: handleAddImageClick,
          }}
        >
          <List>
            <List.Item>{validationError}</List.Item>
          </List>
        </Banner>
      </Modal.Section>
    );
  const imageSizeMarkup =
    activeImage == null ? null : (
      <Modal.Section subdued>
        <Select
          label={i18n.translate('InsertImageModal.size')}
          options={imageSizeOptions}
          value={imageSize === undefined ? ImageSize.ProductImage : imageSize}
          onChange={handleImageSizeChange}
        />
      </Modal.Section>
    );
  const imageAltTextMarkup =
    selectedTab === Tab.UploadedImages && activeImage != null ? (
      <Modal.Section subdued>
        <TextField
          testID="imageAltTextTextField"
          autoComplete="off"
          label={i18n.translate('InsertImageModal.altTextLabel')}
          value={uploadedImagesAltText}
          onChange={handleUploadedImagesAltTextChange}
          helpText={i18n.translate('InsertImageModal.altTextHelpText', {
            altTextLink: (
              <Link
                url={url.helpCenterUrl({
                  locale: i18n.locale,
                  path: 'manual/products/product-media/add-alt-text',
                })}
                target="_blank"
              >
                {i18n.translate('InsertImageModal.altText')}
              </Link>
            ),
          })}
        />
      </Modal.Section>
    ) : null;
  const primaryActionEnabled = activeImage != null || isValid(imageUrl);
  if (data && !queryLoading && data.shop) {
    const {
      shop: {productImages, uploadedImages},
    } = data;
    const productImagesList: Image[] = nodesFromEdges(productImages.edges);
    const uploadedImagesList: Image[] = nodesFromEdges(uploadedImages.edges);
    switch (selectedTab) {
      case Tab.UploadedImages: {
        paginationMarkup =
          uploadedImages.pageInfo.hasNextPage ||
          uploadedImages.pageInfo.hasPreviousPage ? (
            <Pagination
              hasPrevious={
                !uploadInProgress && uploadedImages.pageInfo.hasPreviousPage
              }
              onPrevious={handlePaginateUploadedImagesPrevious}
              hasNext={!uploadInProgress && uploadedImages.pageInfo.hasNextPage}
              onNext={handlePaginateUploadedImagesNext}
            />
          ) : null;
        currentTabMarkup =
          uploadedImagesList.length > 0 ? (
            <ImageGrid
              images={uploadedImagesList}
              selected={activeImage && activeImage.id ? [activeImage.id] : null}
              onImageClick={onImageClick}
            />
          ) : (
            <EmptySearchResult
              title={i18n.translate('InsertImageModal.uploadedImagesEmpty')}
              withIllustration
            />
          );
        break;
      }
      case Tab.ProductImages: {
        paginationMarkup =
          productImages.pageInfo.hasNextPage ||
          productImages.pageInfo.hasPreviousPage ? (
            <Pagination
              hasPrevious={
                !uploadInProgress && productImages.pageInfo.hasPreviousPage
              }
              onPrevious={handlePaginateProductImagesPrevious}
              hasNext={!uploadInProgress && productImages.pageInfo.hasNextPage}
              onNext={handlePaginateProductImagesNext}
            />
          ) : null;
        currentTabMarkup =
          productImagesList.length > 0 ? (
            <ImageGrid
              images={productImagesList}
              selected={activeImage && activeImage.id ? [activeImage.id] : null}
              onImageClick={onImageClick}
            />
          ) : (
            <EmptySearchResult
              title={i18n.translate('InsertImageModal.productImagesEmpty')}
              withIllustration
            />
          );
        break;
      }
      case Tab.Url: {
        currentTabMarkup = (
          <TextField
            testID="UrlTextField"
            autoComplete="off"
            label={i18n.translate('InsertImageModal.addImageFromUrlLabel')}
            placeholder="http://"
            onChange={handleUrlChange}
            value={imageUrl}
          />
        );
        break;
      }
      default:
        currentTabMarkup = null;
    }
  }
  const secondaryActions =
    selectedTab === Tab.UploadedImages
      ? [
          {
            content: i18n.translate('Common.cancel'),
            onAction: handleOnClose,
          },
          {
            content: i18n.translate('Common.uploadFile'),
            loading: uploadInProgress,
            onAction: handleAddImageClick,
          },
        ]
      : [
          {
            content: i18n.translate('Common.cancel'),
            onAction: handleOnClose,
          },
        ];
  const tabMarkup = (
    <Tabs selected={selectedTab} onSelect={handleTabChange} tabs={tabList}>
      {errorBanner}
      <Modal.Section>{currentTabMarkup}</Modal.Section>
      {imageAltTextMarkup}
      {imageSizeMarkup}
    </Tabs>
  );
  const fileUploadMarkup =
    selectedTab === Tab.UploadedImages ? (
      <FileUpload onDrop={processSelectedImage}>{tabMarkup}</FileUpload>
    ) : (
      tabMarkup
    );

  app.loading(loading);

  return (
    <>
      {imageUploaderMarkup}
      <Modal
        title={i18n.translate('InsertImageModal.insertImage')}
        open={open}
        onClose={handleOnClose}
        primaryAction={{
          content: i18n.translate('InsertImageModal.insertImage'),
          onAction: handleInsertImageAction,
          accessibilityLabel: i18n.translate('InsertImageModal.insertImage'),
          disabled: !primaryActionEnabled,
        }}
        secondaryActions={secondaryActions}
        footer={paginationMarkup}
        loading={!data || queryLoading || !data.shop || uploadInProgress}
      >
        <Text visuallyHidden variant="bodySm" as="span">
          <input
            ref={fileUploadInput}
            id="image-upload"
            type="file"
            accept="image/*"
            onChange={handleChange}
            onClick={handleClick}
            testID="UploadFileInput"
          />
        </Text>
        {fileUploadMarkup}
      </Modal>
    </>
  );
}

function handleClick(event: MouseEvent<HTMLInputElement>) {
  (event.target as any).value = null;
}

export const InsertImageModal = memo(InsertImageModalComponent);
